import React, {FC, useCallback, useEffect, useRef} from 'react';
import styled from 'styled-components';

import {IframeFeaturePoliciesEnum} from '../../../libs/shared/helpers/src/browser/browserHelpers';
import {getWindow} from '../../../libs/shared/helpers/src/browser/domHelpers';
import {
  EventHandler,
  HandlerOf,
  MouseEventHandler,
} from '../../../libs/shared/helpers/src/react/reactHelpers';

/*
 * Props.
 */

// The isolated prop is whether the frame should be visible or not.
export interface IframeProps {
  isVisible?: boolean;
  endpoint: string;
  title: string;
  name?: string;
  onMessage?: HandlerOf<MessageEvent>;
  onLoad?: EventHandler<HTMLIFrameElement>;
  onMouseEnter?: MouseEventHandler;
  onMouseLeave?: MouseEventHandler;
  sandboxPermissions?: string;
  featurePolicies?: ReadonlyArray<IframeFeaturePoliciesEnum>;
}

/*
 * Style.
 */

interface IframeStyleProps {
  $isVisible?: boolean;
}
const StyledMainFrame = styled.iframe<IframeStyleProps>`
  position: absolute;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  border: 0;
  display: ${(p) => (p.$isVisible ? 'block' : 'none')};
`;

/*
 * Component.
 */

export const Iframe: FC<IframeProps> = (props) => {
  const {
    isVisible,
    endpoint,
    title,
    name,
    onMessage,
    onLoad,
    onMouseEnter,
    onMouseLeave,
    sandboxPermissions,
    featurePolicies,
  } = props;
  const iframeRef = useRef<HTMLIFrameElement>(null);

  // Filter messages to only listen to our own frame.
  const onMessageFilter = useCallback(
    (event: MessageEvent) => {
      const currentIframe = iframeRef.current;
      if (!currentIframe || event.source !== currentIframe.contentWindow || !onMessage) {
        return;
      }

      onMessage(event);
    },
    [onMessage],
  );

  // Listen to messages coming from that iframe.
  useEffect(() => {
    if (!onMessage) {
      return undefined;
    }

    const currentIframe = iframeRef.current;
    if (!currentIframe) {
      throw new Error('A ref could not be found for this plugin iframe.');
    }

    const window = getWindow(currentIframe);
    if (!window) {
      throw new Error('A window could not be found for this plugin frame.');
    }

    window.addEventListener('message', onMessageFilter);
    return () => window.removeEventListener('message', onMessageFilter);
  }, [onMessage, onMessageFilter]);

  return (
    <StyledMainFrame
      ref={iframeRef}
      src={endpoint}
      frameBorder="0"
      title={title}
      name={name}
      $isVisible={isVisible}
      allow={featurePolicies && featurePolicies.join('; ')}
      allowFullScreen={featurePolicies && featurePolicies.includes(IframeFeaturePoliciesEnum.FULLSCREEN)}
      referrerPolicy="origin-when-cross-origin"
      onLoad={onLoad}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      sandbox={sandboxPermissions}
    />
  );
};
